import React, { useState, useEffect, useRef } from 'react';
import './TextChat.css';
import { useAuth0 } from '@auth0/auth0-react';
import avatarImg from '../images/Job_avatar.webp';

// NIEUW: Component om AI-berichten te formatteren met headers en bullets
const FormattedMessage = ({ content }) => {
  // Check of de content een string is voordat we split gebruiken
  if (typeof content !== 'string') {
    return <p>{content}</p>;
  }

  const lines = content.split('\n').filter(line => line.trim() !== '');

  return (
    <div>
      {lines.map((line, index) => {
        // Herken **Header**
        if (line.startsWith('**') && line.endsWith('**')) {
          return <h4 key={index} className="chat-message-header">{line.substring(2, line.length - 2)}</h4>;
        }
        // Herken bullet points
        if (line.trim().startsWith('•') || line.trim().startsWith('*')) {
          return <li key={index} className="chat-message-bullet">{line.trim().substring(1).trim()}</li>;
        }
        // Normale paragraaf
        return <p key={index}>{line}</p>;
      })}
    </div>
  );
};
const TextChat = ({ isVisible, onClose, initialText = '', fileName = '', onVacancyUpdated }) => {
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const messagesEndRef = useRef(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (initialText && fileName && isVisible) {
      // Start direct met verwerking - NIET de originele tekst tonen
      setIsProcessingUpload(true);
      
      // Voeg verwerkingsbericht toe
      setConversation([
        {
          role: 'system',
          content: fileName,
          icon: 'description',
          timestamp: Date.now()
        },
        {
          role: 'assistant',
          content: 'processing', // Special flag voor loading state
          timestamp: Date.now()
        }
      ]);

      // Start automatisch met analyse en verbetering
      processUploadedText(initialText, fileName);
    }
  }, [initialText, fileName, isVisible]);

  const processUploadedText = async (text, fileName) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch('/api/vacancy-improve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          originalText: text,
          fileName: fileName
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Vervang het loading bericht met het resultaat
      setConversation(prev => [
        prev[0], // Behoud upload melding
        {
          role: 'assistant',
          content: data.improvedText || 'Kon geen verbeterde tekst genereren. Probeer het opnieuw.',
          timestamp: Date.now(),
          isImproved: !!data.improvedText
        }
      ]);

    } catch (error) {
      console.error('Upload processing error:', error);
      setConversation(prev => [
        prev[0],
        {
          role: 'assistant',
          content: 'Sorry, er ging iets mis bij het verwerken van je vacaturetekst. Kun je het opnieuw proberen of de tekst handmatig plakken?',
          timestamp: Date.now()
        }
      ]);
    } finally {
      setIsProcessingUpload(false);
    }
  };

  // VERVANG in TextChat.jsx de sendMessage functie door deze verbeterde versie:

  const sendMessage = async () => {
    if (!message.trim() || isLoading || isProcessingUpload) return;

    // Voeg gebruikersbericht toe aan conversatie
    const newUserMessage = { 
      role: 'user', 
      content: message.trim(),
      timestamp: Date.now()
    };
    
    // Update conversation state DIRECT
    const updatedConversation = [...conversation, newUserMessage];
    setConversation(updatedConversation);
    setMessage('');
    setIsLoading(true);

    try {
      // VERBETERDE API CALL - altijd de volledige context meesturen
      const requestBody = {
        message: message.trim(),
        conversation: updatedConversation.filter(msg => msg.content && msg.content !== 'processing'), // Filter processing en lege berichten uit
        fileName: fileName || undefined
      };

      // BELANGRIJKE FIX: Als er een verbeterde tekst is, stuur die mee als context
      if (conversation.length > 0) {
        // Zoek de laatste AI response die een volledige vacaturetekst bevat
        const lastBotMessage = updatedConversation
          .filter(msg => msg.role === 'assistant' && msg.content && msg.content.length > 300)
          .pop();
        
        if (lastBotMessage) {
          requestBody.fullVacancyContext = lastBotMessage.content;
          console.log('📝 Adding latest improved vacancy as context');
        }
      }

      // Als het de eerste keer is EN we hebben uploaded text, gebruik die als context
      if (updatedConversation.length <= 2 && fileName) {
        console.log('📄 First message after upload - adding file context');
        // De originele tekst is al verwerkt in de eerste AI response, dus gebruik die
      }

      console.log('🔄 Sending request with conversation length:', updatedConversation.length);

      const token = await getAccessTokenSilently();
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Voeg AI response toe aan conversatie
      const newAssistantMessage = {
        role: 'assistant',
        content: data.reply,
        timestamp: Date.now(),
        isImproved: data.isNewVersion || false
      };

      setConversation(prev => [...prev, newAssistantMessage]);

      // Als er een nieuwe/verbeterde versie is, update de parent component
      if (data.isNewVersion || (data.reply.length > 400 && data.reply.includes('**'))) {
        console.log('📝 New improved vacancy version detected');
        
        if (onVacancyUpdated) {
          onVacancyUpdated({
            updatedText: data.reply,
            source: 'text_chat_improvement',
            originalFileName: fileName,
            conversationLength: updatedConversation.length + 1
          });
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      
      // Voeg error message toe
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, er ging iets mis. Probeer het opnieuw.',
        timestamp: Date.now(),
        isError: true
      }]);
    }
    
    setIsLoading(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Toon success feedback (kleine toast)
      showCopyFeedback();
    } catch (error) {
      console.error('Copy failed:', error);
      // Fallback voor oudere browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showCopyFeedback();
    }
  };

  const showCopyFeedback = () => {
    // Kleine toast voor copy feedback
    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.innerHTML = `
      <span class="toast-icon success">
        <span class="material-icons">check_circle</span>
      </span>
      <span>Gekopieerd naar klembord!</span>
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 2000);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, isLoading]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isVisible) return null;

  return (
    <div className="text-chat-overlay">
      <div className="text-chat-panel">
        <div className="text-chat-header">
        <div className="chat-header-content">
            <h3>
              <span className="material-icons" style={{ verticalAlign: 'middle' }}>chat</span>
              Verbeteren met Job
            </h3>
            {fileName && (
            <span className="file-indicator">
                {fileName}
            </span>
            )}
        </div>
        <button className="chat-close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
        </button>
        </div>

        <div className="text-chat-messages">  
        {conversation.map((msg, i) => (
            <div key={i} className={`chat-message ${msg.role}`}>
            {/* System messages (upload notifications) */}
            {msg.role === 'system' && (
                <div className="system-message">
                <span className="material-icons" style={{ fontSize: '1rem', color: '#10b981' }}>
                  {msg.icon || 'check_circle'}
                </span>
                <span>{msg.content} geüpload</span>
                </div>
            )}

            {/* Zichtbare berichten (User & Assistant) */}
            {(msg.role === 'assistant' || msg.role === 'user') && (
                <>
                <div className={`message-avatar ${msg.role}`}>{msg.role === 'assistant' && <img src={avatarImg} alt="Job" />}</div>
                <div className="message-bubble-wrapper">
                    <div className="message-bubble">
                    <div className="message-header">
                        <strong>{msg.role === 'user' ? 'Jij' : 'Job'}</strong>
                    </div>
                    <div className="message-content">
                        {msg.content === 'processing' ? (
                        <div className="processing-message">
                            <div className="processing-spinner"></div>
                            <div className="processing-text">
                            <p><strong>Job analyseert je vacaturetekst...</strong></p>
                            <p>Ik ga de tekst verbeteren met concretere werkzaamheden, echte voordelen en mijn directe stijl. Even geduld!</p>
                            </div>
                        </div>
                        ) : (
                        // Gebruik FormattedMessage voor de AI, simpele <p> voor gebruiker
                        msg.role === 'assistant' 
                            ? <FormattedMessage content={msg.content} />
                            : <p>{msg.content}</p>
                        )}
                    </div>
                    </div>
                    {/* NIEUW: Kopieerknop voor alle AI-berichten */}
                    {msg.role === 'assistant' && msg.content !== 'processing' && (
                    <button
                        className="chat-copy-btn"
                        title="Kopieer bericht"
                        onClick={() => copyToClipboard(msg.content)}
                    >
                        <span className="material-icons">content_copy</span>
                    </button>
                    )}
                </div>
                </>
            )}
            </div>
        ))}
          
          {/* Loading state voor nieuwe berichten */}
          {isLoading && (
            <div className="chat-message assistant">
              <div className="message-avatar assistant"><img src={avatarImg} alt="Job" /></div>
              <div className="message-bubble">
                <div className="message-header">
                  <strong>Job</strong>
                </div>
                <div className="message-content loading">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p>Job denkt na over je verzoek...</p>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="text-chat-input">
          <div className="input-wrapper">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                isProcessingUpload ? "Even geduld, Job verwerkt je bestand..." :
                conversation.length <= 2 ? "Wat vind je van de verbeterde versie? Wat moet anders?" :
                "Beschrijf welke aanpassing je wilt..."
              }
              disabled={isLoading || isProcessingUpload}
            />
            <button 
              onClick={sendMessage} 
              disabled={isLoading || !message.trim() || isProcessingUpload}
              className="send-button"
            >
              <span className="material-icons">send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextChat;